import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    PopoverContainer,
    PopoverTrigger,
    PopoverOverlay,
    PopoverContent,
    PopoverHeader,
    PopoverCloseButton,
    PopoverBody,
} from './InfoPopover.styles';

interface InfoPopoverProps {
    /** The clickable label text (used when no custom trigger) */
    label?: string;
    /** Custom trigger element (replaces label when provided) */
    trigger?: React.ReactNode;
    /** Title displayed in the popover header */
    title: string;
    /** Content to render inside the popover body */
    children: React.ReactNode;
}

/**
 * A click-triggered popover component for displaying contextual information.
 * Uses a portal to render outside overflow:hidden containers.
 * Used for FOSFA, EU, NIOP allowance explanations and file download links.
 */
const InfoPopover: React.FC<InfoPopoverProps> = ({ label, trigger, title, children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLAnchorElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    const close = useCallback(() => setIsOpen(false), []);

    const toggle = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOpen((prev) => !prev);
        },
        []
    );

    // Calculate position based on trigger element
    useEffect(() => {
        if (isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            let left = rect.left;
            let top = rect.bottom + 6;

            // Ensure the popover doesn't go off the right edge
            const popoverWidth = 360;
            if (left + popoverWidth > window.innerWidth - 16) {
                left = window.innerWidth - popoverWidth - 16;
            }
            if (left < 8) left = 8;

            // Ensure the popover doesn't go below the viewport
            const maxBottom = window.innerHeight - 16;
            const estimatedHeight = Math.min(window.innerHeight * 0.4 + 80, 500); // 40vh body + header
            if (top + estimatedHeight > maxBottom) {
                top = Math.max(8, maxBottom - estimatedHeight);
            }

            setPosition({ top, left });
        }
    }, [isOpen]);

    // Close on Escape key
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, close]);

    return (
        <PopoverContainer>
            {trigger ? (
                <span ref={triggerRef as React.RefObject<HTMLSpanElement>} onClick={toggle} role="button" tabIndex={0} style={{ cursor: 'pointer' }}>
                    {trigger}
                </span>
            ) : (
                <PopoverTrigger ref={triggerRef} onClick={toggle} role="button" tabIndex={0}>
                    {label}
                </PopoverTrigger>
            )}
            {isOpen &&
                createPortal(
                    <>
                        <PopoverOverlay onClick={close} />
                        <PopoverContent
                            ref={contentRef}
                            style={{ top: position.top, left: position.left }}
                        >
                            <PopoverHeader>
                                {title}
                                <PopoverCloseButton onClick={close} aria-label="Close">
                                    ×
                                </PopoverCloseButton>
                            </PopoverHeader>
                            <PopoverBody>{children}</PopoverBody>
                        </PopoverContent>
                    </>,
                    document.body
                )}
        </PopoverContainer>
    );
};

export default InfoPopover;
